// src/pages/PagesUtilisateurs/DashboardUsers/modules/NotificationsDisplay.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaBell,
  FaCheck,
  FaCheckDouble,
  FaEyeSlash,
  FaTrash,
} from "react-icons/fa";

import { toast } from "react-toastify";

import {
  deleteNotificationById,
  getNotificationsByUser,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "../../../../services/notificationServices";

import { useNotification } from "../../../../hooks/NotificationContext";

import DashboardActionButton from "../ui/DashboardActionButton";
import DashboardEmptyState from "../ui/DashboardEmptyState";

import {
  formatDateTime,
  isReadFlag,
} from "../utils/dashboardUser.utils";

export default function NotificationsDisplay({
  userId,
  onCounts,
}) {
  const [notifications, setNotifications] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const { setUnreadCount } =
    useNotification();

  /* ---------------------------------------------------------------------- */
  /* Chargement                                                             */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!userId) return;

      try {
        setLoading(true);

        const data =
          await getNotificationsByUser(
            userId,
          );

        if (!mounted) return;

        setNotifications(
          Array.isArray(data)
            ? data
            : [],
        );
      } catch (error) {
        console.error(
          "Erreur chargement notifications :",
          error,
        );

        toast.error(
          "Erreur lors du chargement des notifications.",
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [userId]);

  /* ---------------------------------------------------------------------- */
  /* Compteurs                                                              */
  /* ---------------------------------------------------------------------- */

  const { total, unread } =
    useMemo(() => {
      const totalValue =
        notifications.length;

      const unreadValue =
        notifications.filter(
          (notification) =>
            !isReadFlag(
              notification.read_notification,
            ),
        ).length;

      return {
        total: totalValue,
        unread: unreadValue,
      };
    }, [notifications]);

  useEffect(() => {
    setUnreadCount(unread);

    onCounts?.({
      total,
      unread,
    });
  }, [
    unread,
    total,
    setUnreadCount,
    onCounts,
  ]);

  /* ---------------------------------------------------------------------- */
  /* Actions                                                                */
  /* ---------------------------------------------------------------------- */

  const toggleReadStatus = async (id) => {
    try {
      await markNotificationAsRead(id);

      setNotifications((previous) =>
        previous.map((notification) =>
          notification.id === id
            ? {
                ...notification,

                read_notification:
                  !isReadFlag(
                    notification.read_notification,
                  ),
              }
            : notification,
        ),
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Impossible de modifier la notification.",
      );
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNotificationById(id);

      setNotifications((previous) =>
        previous.filter(
          (notification) =>
            notification.id !== id,
        ),
      );

      toast.success(
        "Notification supprimée.",
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Erreur lors de la suppression.",
      );
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!unread) return;

    try {
      await markAllNotificationsAsRead(
        userId,
      );

      setNotifications((previous) =>
        previous.map(
          (notification) => ({
            ...notification,
            read_notification: true,
          }),
        ),
      );

      toast.success(
        "Toutes les notifications sont marquées comme lues.",
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Impossible de tout marquer comme lu.",
      );
    }
  };

  const handleDeleteAll = async () => {
    if (!notifications.length) return;

    if (
      !window.confirm(
        "Supprimer toutes les notifications ?",
      )
    ) {
      return;
    }

    try {
      await Promise.all(
        notifications.map(
          (notification) =>
            deleteNotificationById(
              notification.id,
            ),
        ),
      );

      setNotifications([]);

      toast.success(
        "Toutes les notifications ont été supprimées.",
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Erreur lors de la suppression.",
      );
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Chargement                                                             */
  /* ---------------------------------------------------------------------- */

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({
          length: 5,
        }).map((_, index) => (
          <div
            key={index}
            className="h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* -------------------------------------------------------------- */}
      {/* Toolbar                                                        */}
      {/* -------------------------------------------------------------- */}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-bold text-slate-900 dark:text-white">
            {total} notification
            {total > 1 ? "s" : ""}
          </p>

          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {unread > 0
              ? `${unread} non lue${
                  unread > 1 ? "s" : ""
                }`
              : "Tout est à jour"}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <DashboardActionButton
            icon={FaCheckDouble}
            onClick={
              handleMarkAllAsRead
            }
            disabled={!unread}
            title="Tout marquer comme lu"
          >
            Tout marquer lu
          </DashboardActionButton>

          <DashboardActionButton
            icon={FaTrash}
            variant="danger"
            onClick={handleDeleteAll}
            disabled={!total}
            title="Tout supprimer"
          >
            Tout supprimer
          </DashboardActionButton>
        </div>
      </div>

      {/* -------------------------------------------------------------- */}
      {/* Vide                                                           */}
      {/* -------------------------------------------------------------- */}

      {!notifications.length ? (
        <DashboardEmptyState
          icon={FaBell}
          title="Aucune notification"
          description="Les informations importantes du club apparaîtront ici."
        />
      ) : (
        <>
          {/* ---------------------------------------------------------- */}
          {/* Desktop                                                    */}
          {/* ---------------------------------------------------------- */}

          <div className="hidden overflow-hidden rounded-2xl border border-slate-200 md:block dark:border-slate-800">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-950/50">
                <tr className="text-left text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                  <th className="w-24 px-4 py-3">
                    Date
                  </th>

                  <th className="px-4 py-3">
                    Notification
                  </th>

                  <th className="w-28 px-4 py-3 text-right">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {notifications.map(
                  (notification) => {
                    const read =
                      isReadFlag(
                        notification.read_notification,
                      );

                    const {
                      date,
                      time,
                    } = formatDateTime(
                      notification.created_at,
                    );

                    return (
                      <tr
                        key={
                          notification.id
                        }
                        className={`
                          transition
                          hover:bg-slate-50
                          dark:hover:bg-slate-800/50
                          ${
                            read
                              ? ""
                              : "bg-blue-50/40 dark:bg-blue-500/5"
                          }
                        `}
                      >
                        <td className="px-4 py-3 align-top">
                          <p className="text-xs font-bold text-slate-700 dark:text-slate-200">
                            {date}
                          </p>

                          <p className="mt-0.5 text-[10px] text-slate-400">
                            {time}
                          </p>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex gap-3">
                            {!read ? (
                              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                            ) : null}

                            <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                              {
                                notification.message
                              }
                            </p>
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <DashboardActionButton
                              compact
                              icon={
                                read
                                  ? FaEyeSlash
                                  : FaCheck
                              }
                              onClick={() =>
                                toggleReadStatus(
                                  notification.id,
                                )
                              }
                              title={
                                read
                                  ? "Marquer comme non lue"
                                  : "Marquer comme lue"
                              }
                            />

                            <DashboardActionButton
                              compact
                              icon={FaTrash}
                              variant="danger"
                              onClick={() =>
                                handleDelete(
                                  notification.id,
                                )
                              }
                              title="Supprimer"
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  },
                )}
              </tbody>
            </table>
          </div>

          {/* ---------------------------------------------------------- */}
          {/* Mobile                                                     */}
          {/* ---------------------------------------------------------- */}

          <div className="space-y-3 md:hidden">
            {notifications.map(
              (notification) => {
                const read =
                  isReadFlag(
                    notification.read_notification,
                  );

                const {
                  date,
                  time,
                } = formatDateTime(
                  notification.created_at,
                );

                return (
                  <article
                    key={notification.id}
                    className={`
                      rounded-2xl border p-4
                      ${
                        read
                          ? "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
                          : "border-blue-200 bg-blue-50/50 dark:border-blue-500/20 dark:bg-blue-500/5"
                      }
                    `}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-bold text-slate-600 dark:text-slate-300">
                          {date}
                        </p>

                        <p className="text-[10px] text-slate-400">
                          {time}
                        </p>
                      </div>

                      {!read ? (
                        <span className="rounded-full bg-blue-100 px-2 py-1 text-[9px] font-black uppercase text-[#013E8E] dark:bg-blue-500/10 dark:text-blue-300">
                          Nouvelle
                        </span>
                      ) : null}
                    </div>

                    <p className="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-300">
                      {notification.message}
                    </p>

                    <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
                      <DashboardActionButton
                        icon={
                          read
                            ? FaEyeSlash
                            : FaCheck
                        }
                        onClick={() =>
                          toggleReadStatus(
                            notification.id,
                          )
                        }
                      >
                        {read
                          ? "Non lue"
                          : "Marquer lue"}
                      </DashboardActionButton>

                      <DashboardActionButton
                        compact
                        icon={FaTrash}
                        variant="danger"
                        onClick={() =>
                          handleDelete(
                            notification.id,
                          )
                        }
                      />
                    </div>
                  </article>
                );
              },
            )}
          </div>
        </>
      )}
    </div>
  );
}